import { create } from 'zustand';
import { acceptCircleAgreement } from '../services/circle-agreement.service';
import { createFlow, onSessionChange, withSession } from './session-read';

interface CircleAgreementState {
  isOpen: boolean;
  isSubmitting: boolean;
  error: string | null;
  resolvers: Array<(accepted: boolean) => void>;

  request: () => Promise<boolean>;
  accept: () => Promise<void>;
  decline: () => void;
  /**
   * Back / Escape: decline, unless the accept POST is in flight (then
   * nothing: the server's answer settles the parked request). Explicit
   * buttons and the session/page rules still call `decline` (backlog c-065).
   * True when it declined.
   */
  dismiss: () => boolean;
}

/**
 * The sheet is a FLOW (session-read.ts createFlow): each opening is an
 * invocation with a token, owned by the session that opened it. Anything that
 * completes after an await (the acceptance POST) settles the sheet only if its
 * invocation still owns it. Reading the CURRENT resolvers without that check
 * let A's in-flight Continue close B's newer sheet and accept B's request
 * (Codex r6 on #940).
 */
const sheet = createFlow();

export const useCircleAgreementStore = create<CircleAgreementState>((set, get) => ({
  isOpen: false,
  isSubmitting: false,
  error: null,
  resolvers: [],

  request: () =>
    new Promise<boolean>((resolve) => {
      if (get().isOpen && sheet.owns(sheet.current())) {
        // This session's sheet is already up: park beside it.
        set((state) => ({ resolvers: [...state.resolvers, resolve] }));
        return;
      }
      // A new invocation. Anything left parked by an invocation that no longer
      // owns the sheet (an ended session) is declined, never inherited.
      const stale = get().resolvers;
      sheet.open();
      set({ isOpen: true, isSubmitting: false, error: null, resolvers: [resolve] });
      stale.forEach((settle) => settle(false));
    }),

  accept: async () => {
    const owner = sheet.current();
    if (!sheet.owns(owner)) {
      // Opened for a session that has ended: Continue must not record an
      // agreement for whoever is signed in now.
      get().decline();
      return;
    }
    set({ isSubmitting: true, error: null });
    const outcome = await withSession(() => acceptCircleAgreement());
    // Another invocation owns the sheet now (or nobody does): nothing here is
    // ours to close, report or resolve.
    if (!sheet.owns(owner)) return;
    if (outcome.status !== 'ok') {
      // Acceptance failed to record. Do NOT let them through: the recorded row is
      // the whole point of this gate, and a session joined without it proves nothing.
      // The modal stays up — but SAY so, or the button just silently springs back
      // to "Continue" and the user has no idea why nothing happened.
      set({
        isSubmitting: false,
        error: "We couldn't save your agreement. Check your connection and try again.",
      });
      return;
    }
    // Still this invocation's sheet, so every parked request is this session's.
    const { resolvers } = get();
    sheet.close();
    set({ isOpen: false, isSubmitting: false, resolvers: [] });
    resolvers.forEach((settle) => settle(true));
  },

  decline: () => {
    const { resolvers } = get();
    sheet.close();
    set({ isOpen: false, isSubmitting: false, error: null, resolvers: [] });
    resolvers.forEach((settle) => settle(false));
  },

  dismiss: () => {
    // Read at the press, not off a render: an accept whose POST has left
    // would land on the server while the parked request was declined here,
    // never replayed (backlog c-065).
    if (get().isSubmitting) return false;
    get().decline();
    return true;
  },
}));

// A session change declines every parked request and closes the sheet.
onSessionChange(() => useCircleAgreementStore.getState().decline());
